/**
 * 이상한 방 - 로컬 날짜 키(`YYYY-MM-DD`)
 *
 * 근거 문서: 로드맵 확정안 4절 6번(매일의 방 · 연속 출석) · 3.1절(핀 표의 `dailyFinish`·`streakDay`) ·
 *           `이상한방-상태모델-v0.1.md` 11.3절(서버가 쥐는 것) · `core/rng.ts` 머리말(생성 경로는 시계를 읽지 않는다)
 *
 * ## 시계를 읽는 **새** 자리는 이 파일 하나다
 *
 * 매일의 방은 "오늘이 며칠인가"를 알아야 하는데, 그것을 아는 순간 그 모듈은 더 이상 순수하지 않다.
 * 그래서 날짜는 여기서 **문자열 하나**로 굳어 아래로 내려간다 - `game/daily.ts`도 `game/economy/**`도
 * `Date`를 모르고 `dateKey`를 인자로 받을 뿐이다(`daily.test.ts`·`economy.test.ts`의 소스 스캔).
 *
 * 규칙의 뜻은 "리듀서·경제·게임 규칙 모듈이 시계를 읽지 않는다"이지 저장소 전체가 0이라는 뜻이 아니다.
 * 이미 시계를 읽는 자리 다섯은 규칙 밖의 것들이라 그대로 둔다(`dateKey.test.ts`의 허용 목록):
 *
 *  - `app/debugHooks.ts` - 프레임 계측(측정 도구)
 *  - `input/editor.ts` - 저장 파일 이름의 세션 태그
 *  - `input/pointerInput.ts` - 주입식 `now`의 기본값(테스트는 가짜 시계를 넣는다)
 *  - `audio/haptics.ts` - 입력 시각(단조 시계 우선)
 *  - `ui/envelopePhases.ts` - 릴이 도는 동안의 소리 커서가 남은 간격을 다시 재는 자리(`envelopeReelSound.ts`가
 *    주입으로 받는다). 연출의 예약이 밀린 만큼을 보정할 뿐이라 여기서 읽은 시각으로 만들어지는 값은 없다
 *
 * 서버 쪽의 짝은 `server/src/clock.ts` 하나이고 그쪽은 `server/src/boundary.test.ts`가 지킨다.
 *
 * ## UTC가 아니라 **로컬** 날짜다
 *
 * 매일의 방이 바뀌는 순간은 사람의 자정이어야 한다. UTC로 자르면 한국에서는 오전 9시에 방이 바뀌고,
 * "어제 방을 아직 못 깼는데 아침에 사라졌다"가 된다. 세계가 같은 방을 **동시에** 받는 것은 이 기능의
 * 목적이 아니다 - 목적은 "오늘 모두가 같은 방을 받는다"(같은 날짜 키 → 같은 시드)이고, 그 비교는
 * 날짜 키가 같은 사람들 사이에서 성립하면 충분하다.
 *
 * ## 되돌린 벽시계는 여기서 막지 않는다
 *
 * 시각을 어제로 돌리면 어제의 키가 나온다. 그것을 막는 것은 이 파일이 아니라 규칙 쪽이다 -
 * `game/daily.ts`가 **문자열 비교**로 "이미 지나간 날"을 가려내고 연속 출석을 늘리지 않는다.
 * 시계를 의심하는 코드를 두 층에 두면 둘이 갈리고, 갈린 날 한쪽이 조용히 관대해진다.
 */

/**
 * 날짜 키의 길이(`YYYY-MM-DD`). **지금 읽는 것은 `dateKey.test.ts`뿐이다** - 모양이 조용히 바뀌지
 * 않았음을 그 테스트가 이 수로 확인한다. 화면이 자리를 재야 할 일이 생기면 여기를 읽어라
 * (없는 소비자를 단언해 두면 다음 사람이 "이미 배선돼 있다"로 읽는다).
 */
export const DATE_KEY_LENGTH = 10

/** 시계. 테스트와 `?date=` 같은 도구가 갈아 끼울 수 있게 함수로 받는다. */
export type Clock = () => Date

/** 두 자리로 0을 채운다. `padStart`가 아니라 조건 하나인 이유는 값이 언제나 1~9 또는 10~99라서다. */
function pad2(value: number): string {
  return value < 10 ? `0${String(value)}` : String(value)
}

/**
 * `Date` → 로컬 날짜 키. **로컬 게터만 쓴다**(`getFullYear`·`getMonth`·`getDate`).
 * `toISOString()`을 쓰면 UTC로 잘려 위 머리말의 결정이 조용히 뒤집힌다 - 그래서 여기서 손으로 짠다.
 */
export function dateKeyOf(date: Date): string {
  const year = date.getFullYear()
  // `getMonth()`는 0부터다. 이 +1을 잊는 것이 날짜 코드에서 가장 흔한 버그라 한 줄로 눈에 띄게 둔다.
  return `${String(year).padStart(4, '0')}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
}

/**
 * 오늘의 날짜 키. **앱은 이것을 한 번만 부르고 그 문자열을 아래로 넘긴다** -
 * 화면마다 다시 부르면 자정을 넘긴 한 판이 두 날짜에 걸치고, 그러면 "완주는 어제 방인데 출석은 오늘"이 된다.
 */
export function todayDateKey(clock: Clock = () => new Date()): string {
  return dateKeyOf(clock())
}
